// tasksService.js
// API service for operational task management

import { HTTPService } from './httpService';
import { completeBody, snoozeBody } from '../util/taskPayloads';

const API = new HTTPService();

// ============================================================================
// Tasks CRUD
// ============================================================================

/**
 * List tasks with filters
 * @param {object} params - { installerId, status, priority, assignedTo, locationId, dueBefore, dueAfter, entityType, entityId, limit, offset }
 */
export const getAll = async (params = {}) => {
    const { data } = await API.get('/api/tasks', params);
    return data.data || data;
};

/**
 * Get single task by ID
 * @param {number} taskId
 */
export const getById = async (taskId) => {
    const { data } = await API.get(`/api/tasks/${taskId}`);
    return data.data || data;
};

/**
 * Create a new task
 * @param {object} payload - { installer_id, title, description, priority, due_date, assigned_to, location_id, entity_type, entity_id, source, recurrence_rule, visibility }
 */
export const create = async (payload) => {
    const { data } = await API.post('/api/tasks', payload);
    return data.data || data;
};

/**
 * Update a task
 * @param {number} taskId
 * @param {object} payload - Fields to update
 */
export const update = async (taskId, payload) => {
    const { data } = await API.patch(`/api/tasks/${taskId}`, payload);
    return data.data || data;
};

/**
 * Complete a task
 * @param {number} taskId
 * @param {number} userId - Who completed it (users.User_id)
 * @param {object} options - { resolution_type, completion_note }
 *   resolution_type is REQUIRED for gated types — invoice_recovery, inbound_call,
 *   consultation. See constants/taskTypeMeta.js for the per-type vocabularies.
 *
 * Body shape lives in util/taskPayloads.js, where it is tested: this call used
 * to send `completed_by`, which the server silently wrote as NULL.
 */
export const complete = async (taskId, userId, options = {}) => {
    const { data } = await API.post(`/api/tasks/${taskId}/complete`, completeBody(userId, options));
    return data.data || data;
};

/**
 * Snooze a task
 * @param {number} taskId
 * @param {string} snoozedUntil - wall-clock 'YYYY-MM-DD HH:mm:ss', must be future
 *
 * Sent as `snoozed_until`. It was `snooze_until` until 2026-08-06, which the
 * server rejected with a 400 on every call — silently, because that response
 * uses `message` and the client read neither key.
 */
export const snooze = async (taskId, snoozedUntil) => {
    const { data } = await API.post(`/api/tasks/${taskId}/snooze`, snoozeBody(snoozedUntil));
    return data.data || data;
};

/**
 * Cancel a task (soft-delete for non-proposed tasks)
 * @param {number} taskId
 */
export const cancel = async (taskId) => {
    const { data } = await API.post(`/api/tasks/${taskId}/cancel`);
    return data.data || data;
};

/**
 * Hard-delete a proposed task (OBD dismissals only)
 * @param {number} taskId
 */
export const deleteTask = async (taskId) => {
    const { data } = await API.delete(`/api/tasks/${taskId}`);
    return data;
};

/**
 * Tasks calendar feed — tasks with due_date in [start, end] for the in-app
 * calendar overlay (separate from the booking calendar; tasks never consume
 * booking capacity).
 * @param {object} params - { installerId, locationId, start, end, userRole, userId }
 */
export const getCalendar = async (params = {}) => {
    const { data } = await API.get('/api/tasks/calendar', params);
    return data.data || data;
};

/**
 * Get counts for badge display
 * @param {number} installerId
 * @param {object} params - Additional filters (status, locationId, etc.)
 */
export const getCounts = async (installerId, params = {}) => {
    const { data } = await API.get('/api/tasks/counts', { installerId, ...params });
    return data.data || data;
};

const TasksAPI = {
    getAll,
    getById,
    create,
    update,
    complete,
    snooze,
    cancel,
    delete: deleteTask,
    getCalendar,
    getCounts
};

export default TasksAPI;
